import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { supportService } from '../services/api';

const Support = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'account_issue', label: 'Account Issue' },
    { value: 'content_issue', label: 'Content Issue' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }
    if (formData.subject.length > 200) {
      setError('Subject must be 200 characters or less');
      return;
    }
    if (formData.message.length > 5000) {
      setError('Message must be 5000 characters or less');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await supportService.sendSupportRequest(formData);
      setSuccess(true);
      setFormData({
        subject: '',
        message: '',
        category: 'general'
      });
    } catch (err) {
      console.error('Error sending support request:', err);
      setError(err.response?.data?.message || 'Failed to send support request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Contact Support
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Please log in to submit a support request. For general inquiries, you can contact us at{' '}
            <strong>support@chscw.club</strong>
          </Alert>
          <Typography variant="body1" color="text.secondary">
            If you're experiencing issues with your account or need help with the platform, 
            please sign in first to access our support form.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Support Request Sent Successfully!
            </Typography>
            <Typography variant="body2">
              Thank you for contacting us. We've received your support request and will respond 
              to your email address ({user.email}) as soon as possible, typically within 24-48 hours.
            </Typography>
          </Alert>
          <Button 
            variant="outlined" 
            onClick={() => setSuccess(false)}
            sx={{ mt: 2 }}
          >
            Send Another Request
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Contact Support
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Need help? Send us a message and we'll get back to you as soon as possible.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
              label="Category"
            >
              {categories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            sx={{ mb: 3 }}
            helperText={`${formData.subject.length}/200 characters`}
            error={formData.subject.length > 200}
          />

          <TextField
            fullWidth
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            multiline
            rows={6}
            sx={{ mb: 3 }}
            helperText={`${formData.message.length}/5000 characters`}
            error={formData.message.length > 5000}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Request'}
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              We'll respond to: {user.email}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Before contacting support:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>Check if your issue is covered in our FAQ</li>
            <li>Try refreshing the page or logging out and back in</li>
            <li>Make sure you're using a supported browser</li>
            <li>Include specific details about what you were doing when the issue occurred</li>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Support;
